import {
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageCard from 'src/components/PageCard';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LoadingIconButton from 'src/components/LoadingIconButton';
import LoadingButton from 'src/components/LoadingButton';
import RoleBased from 'src/components/RoleBased';
import {
  PortalRole,
  useListCustomersQuery,
  useUpdateCustomerDeletedStatusMutation,
} from 'src/backend';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search } from '@mui/icons-material';
import { formatDateTime } from 'src/utils';
import CreateCustomerDialog from './CreateCustomerDialog';
import EditCustomerDialog from './EditCustomerDialog';

export type Customer = {
  id: string;
  name: string;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
};

function CustomToolbar(props: {
  onSearch: (searchText: string | undefined) => void;
}) {
  const { onSearch } = props;
  const [searchText, setSearchText] = useState('');
  return (
    <Stack direction="row-reverse">
      <IconButton
        onClick={() => onSearch(searchText === '' ? undefined : searchText)}
      >
        <Search />
      </IconButton>
      <TextField
        name="search"
        label="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchText === '' ? undefined : searchText);
          }
        }}
      />
    </Stack>
  );
}

function CustomersPanel() {
  const [refetchRequired, setRefetchRequired] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  const [searchFilter, setSearchFilter] = useState<string>('');

  const updateDeletedStatus = useUpdateCustomerDeletedStatusMutation({});
  const { data, isLoading, isFetching, refetch } = useListCustomersQuery(true);

  // Client-side filtering of customers
  const filteredCustomers = useMemo(() => {
    if (!data?.data) return [];

    const customers = data.data as Customer[];

    if (!searchFilter.trim()) {
      return customers;
    }

    const searchTerm = searchFilter.toLowerCase().trim();
    return customers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchTerm) ||
        customer.id.toLowerCase().includes(searchTerm),
    );
  }, [data?.data, searchFilter]);

  const handleDelete = useCallback(() => {
    if (selectedCustomer) {
      updateDeletedStatus[0]({
        customerId: selectedCustomer.id,
        request: { isDeleted: true },
      })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        })
        .finally(() => {
          setSelectedCustomer(null);
          setShowConfirmationModal(false);
        });
    }
  }, [updateDeletedStatus, selectedCustomer]);

  const handleRestore = useCallback(() => {
    if (selectedCustomer) {
      updateDeletedStatus[0]({
        customerId: selectedCustomer.id,
        request: { isDeleted: false },
      })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        })
        .finally(() => {
          setSelectedCustomer(null);
          setShowConfirmationModal(false);
        });
    }
  }, [updateDeletedStatus, selectedCustomer]);

  const handleOpenEditCustomerModal = useCallback((customer: Customer) => {
    setCustomerToEdit(customer);
    setShowEditCustomerModal(true);
  }, []);

  const handleCloseEditCustomerModal = useCallback(() => {
    setShowEditCustomerModal(false);
    setCustomerToEdit(null);
  }, []);

  const handleOpenAddCustomerModal = useCallback(() => {
    setShowAddCustomerModal(true);
  }, []);

  const handleCloseAddCustomerModal = useCallback(() => {
    setShowAddCustomerModal(false);
  }, []);

  const handleCustomerUpdated = useCallback(() => {
    setRefetchRequired(true);
  }, []);

  const onSearch = useCallback((searchText: string | undefined) => {
    setSearchFilter(searchText || '');
  }, []);

  const columns: GridColDef<Customer>[] = [
    {
      field: 'id',
      headerName: 'Customer ID',
    },
    {
      field: 'name',
      headerName: 'Customer Name',
      flex: 1,
    },
    {
      field: 'isDeleted',
      headerName: 'Is Deleted',
      flex: 0.5,
    },
    {
      field: 'lastModifiedAt',
      headerName: 'Last Modified [UTC]',
      flex: 1,
      valueFormatter: (v) => formatDateTime(v.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: (params) => (
        <div>
          {!params.row.isDeleted && (
            <LoadingIconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditCustomerModal(params.row);
              }}
            >
              <Tooltip title="Edit customer">
                <EditOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
          )}
          {!params.row.isDeleted ? (
            <LoadingIconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCustomer(params.row);
                setShowConfirmationModal(true);
              }}
              loading={updateDeletedStatus[1].isLoading}
            >
              <Tooltip title="Delete customer">
                <DeleteOutlineOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
          ) : (
            <LoadingIconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCustomer(params.row);
                setShowConfirmationModal(true);
              }}
              loading={updateDeletedStatus[1].isLoading}
            >
              <Tooltip title="Restore customer">
                <RestoreOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (refetchRequired) {
      setRefetchRequired(false);
      refetch();
    }
  }, [refetchRequired, refetch]);

  return (
    <>
      <Dialog
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
      >
        <DialogTitle sx={{ padding: '2rem', fontSize: '2.5rem' }}>
          {selectedCustomer?.isDeleted ? 'Restore customer' : 'Delete customer'}
        </DialogTitle>
        <DialogContentText sx={{ padding: '2rem', fontSize: '1.5rem' }}>
          Are you sure you want to{' '}
          {selectedCustomer?.isDeleted ? 'restore' : 'delete'} customer:{' '}
          <b>{selectedCustomer?.name}</b>?
          {!selectedCustomer?.isDeleted && (
            <>
              <br />
              This action can be undone later by restoring the customer.
            </>
          )}
        </DialogContentText>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedCustomer(null);
              setShowConfirmationModal(false);
            }}
            autoFocus
          >
            Cancel
          </Button>
          <LoadingButton
            loading={updateDeletedStatus[1].isLoading}
            variant="outlined"
            type="submit"
            onClick={selectedCustomer?.isDeleted ? handleRestore : handleDelete}
            sx={
              selectedCustomer?.isDeleted
                ? {
                    background: '#2e7d32',
                    color: 'white',
                    '&:hover': {
                      background: '#1b5e20',
                      boxShadow:
                        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
                    },
                  }
                : {
                    background: '#f10101',
                    color: 'white',
                    '&:hover': {
                      background: '#ab0e0e',
                      boxShadow:
                        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
                    },
                  }
            }
          >
            {selectedCustomer?.isDeleted ? 'Restore' : 'Confirm'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <CreateCustomerDialog
        open={showAddCustomerModal}
        onClose={handleCloseAddCustomerModal}
        onCustomerCreated={handleCustomerUpdated}
      />

      <EditCustomerDialog
        open={showEditCustomerModal}
        customer={customerToEdit}
        onClose={handleCloseEditCustomerModal}
        onCustomerUpdated={handleCustomerUpdated}
      />

      <PageCard
        title="Customers"
        icon={<TableRowsOutlinedIcon />}
        sx={{
          '& .MuiCardHeader-root': {
            height: '80px',
          },
        }}
        contentSx={{ display: 'block' }}
        action={
          <>
            <RoleBased
              requiredRole={PortalRole[PortalRole.Admin]}
              component={
                <IconButton onClick={handleOpenAddCustomerModal}>
                  <Tooltip title="Add customer">
                    <AddCircleOutlineOutlinedIcon />
                  </Tooltip>
                </IconButton>
              }
            />
            <LoadingIconButton
              loading={isLoading || isFetching}
              onClick={() => setRefetchRequired(true)}
            >
              <Tooltip title="Refresh">
                <CachedOutlinedIcon fontSize="large" />
              </Tooltip>
            </LoadingIconButton>
          </>
        }
      >
        <DataGrid
          columns={columns}
          columnVisibilityModel={{ id: false }}
          rows={filteredCustomers}
          loading={isFetching}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              onSearch,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          sx={{
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              display: 'none',
            },
            '& .MuiDataGrid-overlay': {
              height: '100%',
            },
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .deleted-customer-row': {
              backgroundColor: '#f5f5f5',
              color: '#999',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
              '& .MuiIconButton-root': {
                color: 'inherit',
              },
              '& .MuiIconButton-root svg': {
                color: '#142A4D',
              },
            },
          }}
          getRowClassName={(params) =>
            (params.row as Customer).isDeleted ? 'deleted-customer-row' : ''
          }
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          hideFooterSelectedRowCount
          disableRowSelectionOnClick
          paginationMode="client"
          filterMode="client"
          pageSizeOptions={[10]}
        />
      </PageCard>
    </>
  );
}

export default CustomersPanel;
